import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Table as AntTable, Card, Empty, Modal, Space, Tag, Typography } from "antd";
import { QrcodeOutlined, PlusOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { ListTablesResponse } from "../../types/api";
import { Table } from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";

import { NewTableForm } from "./Form";
import { TableDeleteButton } from "./DeleteButton";
import { TableQRCodeButton } from "./QRCodeButton";
import { SignPreviewButton } from "./SignPreviewButton";
import { TableSignButton, DownloadSignsButton } from "./SignDownloadButton";

const { Text } = Typography;

type QrCodeManagementProps = {
  restaurantId: string;
};

// classifyOrderType buckets a POS order type with the same keyword heuristics
// the backend applies when prioritizing orders (lib/model/util.go).
type OrderTypeBucket = "dine_in" | "to_go" | "other";
const classifyOrderType = (value: string): OrderTypeBucket => {
  const lower = (value || "").toLowerCase();
  if (["togo", "to go", "to-go", "takeout", "take out"].some((k) => lower.includes(k))) {
    return "to_go";
  }
  if (["here", "dine"].some((k) => lower.includes(k))) {
    return "dine_in";
  }
  return "other";
};

export const QrCodeEditor = (props: QrCodeManagementProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();
  const [tables, setTables] = React.useState<Table[]>([]);
  const [fetching, setFetching] = React.useState(true);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [createOpen, setCreateOpen] = React.useState(false);

  const refreshTables = () => {
    setFetching(true);
    getFetchT<ListTablesResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/tables`,
      (result: ListTablesResponse) => {
        // The backend may return `null` for `tables` when no table QR codes
        // exist yet; treat that as an empty list.
        setTables(result.tables ?? []);
        setFetching(false);
      },
      (err) => {
        setFetching(false);
        notifyError(t("unable_to_fetch_tables"), err);
      }
    );
  };

  // Load the current set of tables.
  React.useEffect(refreshTables, [props.restaurantId]);

  const onChange = () => {
    // Drop any selection that may reference tables that no longer exist.
    setSelectedIds([]);
    refreshTables();
  };

  const onCreated = () => {
    setCreateOpen(false);
    onChange();
  };

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys: React.Key[]) => setSelectedIds(keys.map(String)),
  };

  const orderTypeTag = (value: string) => {
    switch (classifyOrderType(value)) {
      case "to_go":
        return <Tag color="orange">{t("to_go")}</Tag>;
      case "dine_in":
        return <Tag color="blue">{t("dine_in")}</Tag>;
      default:
        return value ? <Tag>{value}</Tag> : <Text type="secondary">-</Text>;
    }
  };

  const columns: any = [
    {
      title: t("name"),
      dataIndex: "name",
      sorter: (a: Table, b: Table) => a.name.localeCompare(b.name),
    },
    {
      title: t("order_type"),
      dataIndex: "pos_order_type",
      width: 140,
      render: (value: string) => orderTypeTag(value),
      filters: [
        { text: t("dine_in"), value: "dine_in" },
        { text: t("to_go"), value: "to_go" },
      ],
      onFilter: (value: React.Key | boolean, record: Table) =>
        classifyOrderType(record.pos_order_type) === value,
    },
    {
      title: t("actions"),
      key: "actions",
      width: 200,
      render: (text: string, record: Table) => (
        <Space>
          <SignPreviewButton
            restaurantId={props.restaurantId}
            table_id={record.id}
            table_name={record.name}
          />
          <TableQRCodeButton
            restaurantId={props.restaurantId}
            table_id={record.id}
            table_name={record.name}
          />
          <TableSignButton
            restaurantId={props.restaurantId}
            table_id={record.id}
            table_name={record.name}
          />
          <TableDeleteButton
            restaurantId={props.restaurantId}
            table={record}
            onSuccess={onChange}
          />
        </Space>
      ),
    },
  ];

  const emptyText = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={<Text type="secondary">{t("no_qr_codes_message")}</Text>}
    >
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
        {t("create_new_qr_code")}
      </Button>
    </Empty>
  );

  return (
    <div>
      <Card
        title={
          <Space>
            <QrcodeOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_qr_codes")}
            </Text>
          </Space>
        }
        extra={
          <Space>
            <DownloadSignsButton
              restaurantId={props.restaurantId}
              selectedIds={selectedIds}
              disabled={tables.length === 0}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t("create_new_qr_code")}
            </Button>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <AntTable
          columns={columns}
          dataSource={tables}
          rowKey="id"
          loading={fetching}
          rowSelection={rowSelection}
          locale={{ emptyText }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t("total_qr_codes", { total }),
          }}
          style={{ borderRadius: token.borderRadiusLG }}
        />
      </Card>

      <Modal
        title={t("create_new_qr_code")}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnClose
      >
        <NewTableForm restaurantId={props.restaurantId} onSuccess={onCreated} />
      </Modal>
    </div>
  );
};
